import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Edit, Eye, ExternalLink,
  Home, Info, Video, BookOpen, Calendar, Tv, Users, Crown,
  Layout, Paintbrush, Image, Music
} from 'lucide-react';
import { isAdminAuthenticated } from '@/utils/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Define all editable site pages with their dedicated editors
const SITE_PAGES = [
  { 
    id: 'home', 
    name: 'Home Page', 
    path: '/', 
    icon: Home,
    editorRoute: '/admin/home-editor',
    description: 'Hero section, features, and call-to-action'
  },
  { 
    id: 'about', 
    name: 'About (Escola de Samba Casa Samba)', 
    path: '/about', 
    icon: Users,
    editorRoute: '/admin/casa-samba-about-editor',
    description: 'Organization info, leadership, and members'
  },
  { 
    id: 'legacy', 
    name: 'Legacy (Founder)', 
    path: '/legacy', 
    icon: Crown,
    editorRoute: '/admin/about-editor',
    description: 'Mestre Curtis Pierre\'s story and achievements'
  },
  { 
    id: 'artwork', 
    name: 'Artwork Gallery', 
    path: '/artwork', 
    icon: Paintbrush,
    editorRoute: '/admin/artwork',
    description: 'Manage artwork, prices, and categories'
  },
  { 
    id: 'videos', 
    name: 'Videos Page', 
    path: '/videos', 
    icon: Video,
    editorRoute: '/admin/videos',
    description: 'Video courses and content library'
  },
  { 
    id: 'courses', 
    name: 'Pandeiro Course', 
    path: '/courses/pandeiro', 
    icon: BookOpen,
    editorRoute: '/admin/course-editor',
    description: 'Pandeiro Masterclass sales page'
  },
  { 
    id: 'classes', 
    name: 'Classes Page', 
    path: '/classes', 
    icon: Calendar,
    editorRoute: '/admin/class-editor',
    description: 'Scheduled classes and private lessons'
  },
  { 
    id: 'ppv', 
    name: 'Pay-Per-View', 
    path: '/ppv', 
    icon: Tv,
    editorRoute: '/admin/ppv-editor',
    description: 'Live events and PPV ticketing'
  },
  { 
    id: 'music', 
    name: 'Afro-Brasilian Cultural School', 
    path: '/music', 
    icon: Music,
    editorRoute: '/admin/music',
    description: 'Tracks, albums, and audio content'
  },
  { 
    id: 'media', 
    name: 'Media Library', 
    path: null, 
    icon: Image,
    editorRoute: '/admin/media',
    description: 'Upload and manage all images & files'
  },
];

export default function LiveSiteEditor() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
    }
  }, [navigate]);

  const goToEditor = (page) => {
    navigate(page.editorRoute);
  };

  const viewLivePage = (page, e) => {
    e.stopPropagation();
    if (page.path) {
      window.open(page.path, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-black border-b border-zinc-800 px-6 py-4 flex items-center gap-4 sticky top-0 z-50">
        <button 
          onClick={() => navigate('/admin/dashboard')} 
          className="p-2 hover:bg-zinc-800 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        <div className="flex items-center gap-3">
          <Layout className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Live Site Editor</h1>
            <p className="text-sm text-zinc-400">Click any page to edit its content</p>
          </div>
        </div>
      </div>

      {/* Page Grid */}
      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SITE_PAGES.map((page) => {
            const Icon = page.icon;
            
            return (
              <motion.button
                key={page.id}
                onClick={() => goToEditor(page)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group p-6 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-primary/50 rounded-2xl text-left transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-primary/20 rounded-xl group-hover:bg-primary/30 transition-colors">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {page.path && (
                      <button
                        onClick={(e) => viewLivePage(page, e)}
                        className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        title="View live page"
                      >
                        <Eye className="w-4 h-4 text-zinc-400" />
                      </button>
                    )}
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <Edit className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-primary transition-colors">
                  {page.name}
                </h3>
                <p className="text-sm text-zinc-400 mb-3">
                  {page.description}
                </p>
                
                {page.path && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                      {page.path}
                    </span>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                  <span className="text-sm text-primary font-medium">
                    Click to edit →
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
        
        {/* Quick tip */}
        <div className="mt-8 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <p className="text-sm text-zinc-400 text-center">
            <strong className="text-white">Tip:</strong> Each page has its own dedicated editor. 
            Click the <Eye className="w-4 h-4 inline mx-1" /> icon to preview the live page in a new tab.
          </p>
        </div>
      </div>
    </div>
  );
}
